import { useMemo } from "react";
import { create } from "zustand";
import type { BoothFullConfig, BoothWatermark } from "@fiket/booth-schema";
import { resolveCaptionEnabled } from "@fiket/booth-schema";
import { INACTIVE_PAYMENT, boothPaymentState, type BoothPaymentState } from "../features/Payment/paymentFlow";
import { getWatermarkImage } from "../utils/watermarkImage";
import { trackError } from "../telemetry/activity";

interface BoothConfigState {
  slug: string | null;
  config: BoothFullConfig | null;
  loaded: boolean;
  error: string | null;
  load: (slug: string) => Promise<void>;
  /** 로딩 화면 없이 조용히 재조회 — 어트랙트 복귀 시 콘솔에서 바꾼 프리셋·스타일을 다음 손님에게 반영. 실패는 무시. */
  refresh: () => Promise<void>;
}

export const useBoothConfig = create<BoothConfigState>((set, get) => ({
  slug: null,
  config: null,
  loaded: false,
  error: null,
  load: async (slug: string) => {
    set({ slug, loaded: false, error: null });
    try {
      const res = await fetch(`/api/booth/${slug}/config`, { cache: "no-store" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: { message?: string } };
        set({ loaded: true, error: body?.error?.message ?? "부스를 찾을 수 없습니다." });
        return;
      }
      const body = await res.json() as { success: true; data: BoothFullConfig };
      set({ config: body.data, loaded: true });
      // 워터마크 이미지 프리로드 — 첫 변환 완료 전에 캐시에 넣어 합성 지연을 최소화한다 (BOOTH-103).
      void getWatermarkImage(body.data.booth.watermark?.imageUrl ?? null);
    } catch {
      set({ loaded: true, error: "네트워크 오류" });
    }
  },
  refresh: async () => {
    const { slug, loaded } = get();
    if (!slug || !loaded) return;
    try {
      const res = await fetch(`/api/booth/${slug}/config`, { cache: "no-store" });
      if (!res.ok) {
        trackError("config_refresh_failed", undefined, { message: `HTTP ${res.status}`, httpStatus: res.status, url: `/api/booth/${slug}/config` });
        return;
      }
      const body = await res.json() as { success: true; data: BoothFullConfig };
      set({ config: body.data });
      // 워터마크 이미지 프리로드 (BOOTH-103).
      void getWatermarkImage(body.data.booth.watermark?.imageUrl ?? null);
    } catch (err) {
      /* 다음 복귀 때 재시도 */
      trackError("config_refresh_failed", err);
    }
  },
}));

/** URL pathname에서 boothSlug 추출. `/booth/gangnam` → "gangnam" */
export function extractBoothSlug(): string | null {
  const match = window.location.pathname.match(/^\/booth\/([a-z0-9-]{3,32})/);
  return match?.[1] ?? null;
}

/**
 * 부스 영상(움직이는 화보) 애드온 스위치 (BOOTH-60). `Booth.videoEnabled` 없음 = true.
 * 결과 화면 "영상 만들기" 와 스타일 타일 영상 배지가 함께 본다 — 스타일의 `videoEnabled` 보다 부스가 우선.
 */
export function useBoothVideoEnabled(): boolean {
  return useBoothConfig((s) => s.config?.booth.videoEnabled ?? true);
}

/**
 * 부스 영상 자막 단계 스위치 (BOOTH-85 후속). `Booth.captionEnabled` 없음 = true.
 * 끔이면 영상 플로우가 자막 화면(9.3)을 건너뛴다 — 소개·동작 선택·생성 뒤로가기가 함께 본다(`videoFlow`).
 */
export function useBoothCaptionEnabled(): boolean {
  return useBoothConfig((s) => resolveCaptionEnabled(s.config?.booth));
}

/**
 * 부스 결제 상태 (BOOTH-61/74/80) — 사진·영상 각각 `*PaymentActive`(스위치 ON 그리고 가격 있음) 기반. config 로딩 전엔 비활성.
 * 셀렉터가 매번 새 객체를 만들지 않게 booth 참조로 메모한다.
 */
export function useBoothPayment(): BoothPaymentState {
  const booth = useBoothConfig((s) => s.config?.booth ?? null);
  return useMemo(() => (booth ? boothPaymentState(booth) : INACTIVE_PAYMENT), [booth]);
}

/**
 * 부스 워터마크 설정 (BOOTH-103). `Booth.watermark` 없음 = null (워터마크 없음).
 * 워터마크 이미지는 `useBoothConfig.load`/`refresh` 시 미리 프리로드된다.
 */
export function useBoothWatermark(): BoothWatermark | null {
  return useBoothConfig((s) => s.config?.booth.watermark ?? null);
}
